/**
 * Which agent activity becomes a desktop notification, and what it says.
 * Pure functions so the rules are unit-tested; the wiring lives in
 * useDesktopNotifications.ts and useDesktopAsk.ts.
 */
import type { QueueItem } from '@/agent/api'
import type { AnyAgentEvent, PayloadMap } from '@/agent/protocol/types'
import { clientCallTitle } from './clientTools'
import type { DesktopNotification, NotificationKind, NotificationReply } from './types'

export const NOTIFICATION_BODY_MAX = 180

export function conversationPath(sessionId: string): string {
    return `/dashboard/conversations/${sessionId}`
}

/** The inline Reply a notification offers (macOS); approvals need the full card, so none. */
export function replyFor(sessionId: string, kind: NotificationKind): NotificationReply | undefined {
    if (kind === 'question') return { sessionId, answer: true, placeholder: 'Answer Lighthouse…' }
    if (kind === 'agent') return { sessionId, answer: false, placeholder: 'Reply to Lighthouse…' }
    return undefined
}

/** Markdown-ish agent text → one plain line for a notification body (~180 chars). */
export function notificationBody(text: string | null | undefined, max = NOTIFICATION_BODY_MAX): string {
    const plain = String(text ?? '')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
        .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
        .replace(/[*_`>#~|]+/g, '')
        .replace(/^\s*[-+]\s+/gm, '')
        .replace(/\s+/g, ' ')
        .trim()
    if (plain.length <= max) return plain
    const cut = plain.slice(0, max - 1).replace(/\s+\S*$/, '')
    return `${cut || plain.slice(0, max - 1)}…`
}

/** The kind of attention a queue item asks for. */
export function queueItemKind(item: Pick<QueueItem, 'pending_approval' | 'waiting_question'>): NotificationKind {
    if (item.pending_approval) return 'approval'
    if (item.waiting_question) return 'question'
    return 'agent'
}

export function queueItemNotification(item: QueueItem): DesktopNotification {
    const kind = queueItemKind(item)
    const fallback = kind === 'approval' ? 'Lighthouse needs your approval' : kind === 'question' ? 'Lighthouse has a question' : 'New from Lighthouse'
    return {
        kind,
        tag: `session-${item.session_id}`,
        title: (item.headline || item.title || fallback).trim(),
        body: notificationBody(item.snippet || (item.headline && item.title !== item.headline ? item.title : '') || ''),
        path: conversationPath(item.session_id),
        reply: replyFor(item.session_id, kind),
    }
}

/**
 * The needs-you items that are new since the last look. `seen` null means
 * this is the first hydration: nothing is new (never notify for what was
 * already waiting when the app opened).
 */
export function newNeedsYouItems(seen: ReadonlySet<string> | null, items: QueueItem[]): QueueItem[] {
    if (!seen) return []
    return items.filter((i) => i.kind === 'needs_you' && !seen.has(needsYouKey(i)))
}

/**
 * Identity of a needs-you moment: the same conversation asking again later
 * (a new question after an answered one) is a new moment; a re-poll is not.
 */
export function needsYouKey(item: Pick<QueueItem, 'session_id' | 'pending_approval' | 'waiting_question'>): string {
    return `${item.session_id}:${item.pending_approval ? 'a' : ''}${item.waiting_question ? 'q' : ''}`
}

export function needsYouKeys(items: QueueItem[]): Set<string> {
    return new Set(items.filter((i) => i.kind === 'needs_you').map(needsYouKey))
}

export interface SessionEventContext {
    sessionId: string
    title?: string | null
    /** The last thing the agent said in this run (message.completed, role assistant). */
    lastAssistantText?: string | null
}

/** Events that end a background turn's wait: the agent finished, asked, or needs an approval. */
export function isAttentionEvent(ev: Pick<AnyAgentEvent, 'type'>): boolean {
    return ev.type === 'run.finished' || ev.type === 'ask_user' || ev.type === 'changeset.pending_approval'
}

/** A notification for one of a session's attention events, or null for anything else. */
export function notificationForSessionEvent(ev: AnyAgentEvent, ctx: SessionEventContext): DesktopNotification | null {
    const base = { tag: `ask-${ctx.sessionId}`, path: conversationPath(ctx.sessionId) }
    const title = (ctx.title || '').trim()
    switch (ev.type) {
        case 'ask_user':
            return {
                ...base,
                kind: 'question',
                title: title ? `Question: ${title}` : 'Lighthouse has a question',
                body: notificationBody(ev.payload.question || ctx.lastAssistantText),
                reply: replyFor(ctx.sessionId, 'question'),
            }
        case 'changeset.pending_approval': {
            const summary = (ev.payload.summary ?? []).join(' · ')
            return {
                ...base,
                kind: 'approval',
                title: title ? `Approve: ${title}` : 'Lighthouse needs your approval',
                body: notificationBody(summary || ctx.lastAssistantText || 'Review the changes before they go out.'),
            }
        }
        case 'run.finished': {
            const failed = ev.payload.status === 'failed' || ev.payload.status === 'budget_exhausted'
            if (ev.payload.status === 'cancelled' || ev.payload.status === 'waiting_user') return null
            return {
                ...base,
                kind: 'agent',
                title: failed ? "Lighthouse couldn't finish" : title || 'Lighthouse is done',
                body: notificationBody(failed ? 'Open the conversation to try again.' : ctx.lastAssistantText || 'Your answer is ready.'),
                reply: failed ? undefined : replyFor(ctx.sessionId, 'agent'),
            }
        }
        default:
            return null
    }
}

/** The agent asks to use the creator's computer (`client.call`): an approval, answered on the card (no inline reply). */
export function clientCallNotification(p: PayloadMap['client.call'], sessionId: string): DesktopNotification {
    return {
        kind: 'approval',
        tag: `client-call-${p.call_id}`,
        title: clientCallTitle(p.action, p.args?.url),
        body: notificationBody(p.why) || 'Open the conversation to answer.',
        path: conversationPath(sessionId),
    }
}
